import { getSupabaseClient } from '../lib/supabase';

interface MatchRequest {
  seeker_id: string;
  limit?: number;
}

interface MatchScore {
  guide_id: string;
  total_score: number;
  breakdown: {
    location: number;
    experience_type: number;
    medicine_comfort: number;
    support_type: number;
    travel: number;
    focus_areas: number;
    specialized_groups: number;
    religious_alignment: number;
    medication_compatibility: number;
    clinical_experience: number;
  };
  reasoning: string[];
}

interface MatchResponse {
  seeker_id: string;
  matches: MatchScore[];
  total_guides_evaluated: number;
  timestamp: string;
}

export type StoredMatchStatus = 'active' | 'invalidated' | 'expired' | null;

export type MatchChangeSource = 'change_request' | 'admin' | 'system';

export interface MatchExclusionSummary {
    seeker_id: string;
    guide_id: string;
    source: MatchChangeSource | null;
    reason_id?: string | null;
    note?: string | null;
    created_at?: string | null;
    expires_at?: string | null;
}

export interface StoredMatch {
    id: string;
    seeker_id: string;
    guide_id: string;
    match_score: number;
    score_breakdown: Record<string, number> | null;
    reasoning: string[] | null;
    created_at: string;
    updated_at?: string | null;
    status?: StoredMatchStatus;
    ended_at?: string | null;
    exclusion?: MatchExclusionSummary | null;
    guide_details?: {
        guide_id: string;
        full_name?: string | null;
        email?: string | null;
        phone?: string | null;
        location?: string | null;
        bio?: string | null;
        experience_types?: string[] | null;
        support_types?: string[] | null;
        backgroundChecked?: boolean | null;
        avatarUrl?: string | null;
        about?: string | null;
        yearsExperience?: number | null;
        yearsExperienceText?: string | null;
        guides_enhanced_profile?: {
            image_url?: string | null;
        } | null;
    } | null;
}

function getEnv(): any {
    return (window as any).__ENV__;
}

function getSB() {
    return getSupabaseClient();
}

async function fetchGuideDetails(sb: ReturnType<typeof getSB>, guideId: string | null | undefined): Promise<StoredMatch['guide_details']> {
    if (!guideId) return null;

    const { data: guideRow, error: guideError } = await sb
        .from('guide_matching_view')
        .select('guide_id, full_name, email, phone, location, bio, experience_types, support_types, backgroundChecked')
        .eq('guide_id', guideId)
        .maybeSingle();

    if (guideError) {
        console.warn('Unable to load guide details for match', guideError);
        return null;
    }

    if (!guideRow) {
        return null;
    }

    type GuideEnhancedProfileRow = { image_url: string | null; about: string | null };
    type GuideProfessionalRow = { years_practicing: string | number | null };
    type ProfileRow = { avatar_url: string | null };

    let avatarUrl: string | null = null;
    let about: string | null = null;
    let yearsExperience: number | null = null;
    let yearsExperienceText: string | null = null;

    let enhancedProfileRow: GuideEnhancedProfileRow | null = null;

    const enhancedProfileResult = await sb
        .from('guides_enhanced_profile')
        .select('image_url, about')
        .eq('guide_id', guideId)
        .maybeSingle<GuideEnhancedProfileRow>();

    if (enhancedProfileResult.error) {
        console.warn('Unable to load enhanced guide profile', enhancedProfileResult.error);

        const fallbackProfileResult = await sb
            .from('guides_enhanced_profile')
            .select('image_url, about')
            .eq('guide_id', guideId)
            .maybeSingle<GuideEnhancedProfileRow>();

        if (fallbackProfileResult.error) {
            console.warn('Unable to load enhanced guide profile from fallback table', fallbackProfileResult.error);
        } else {
            enhancedProfileRow = fallbackProfileResult.data ?? null;
        }
    } else {
        enhancedProfileRow = enhancedProfileResult.data ?? null;
    }

    if (enhancedProfileRow) {
        avatarUrl = enhancedProfileRow.image_url ?? null;
        about = enhancedProfileRow.about ?? null;
    }

    if (!avatarUrl) {
        const { data: profileRow, error: profileError } = await sb
            .from('profiles')
            .select('avatar_url')
            .eq('id', guideId)
            .maybeSingle<ProfileRow>();

        if (profileError) {
            console.warn('Unable to load guide avatar', profileError);
        } else if (profileRow) {
            avatarUrl = profileRow.avatar_url ?? null;
        }
    }

    const { data: professionalRow, error: professionalError } = await sb
        .from('guide_professional')
        .select('years_practicing')
        .eq('guide_id', guideId)
        .maybeSingle<GuideProfessionalRow>();

    if (professionalError) {
        console.warn('Unable to load guide professional info', professionalError);
    } else if (professionalRow && professionalRow.years_practicing != null) {
        const rawValue = professionalRow.years_practicing;

        if (typeof rawValue === 'number' && !Number.isNaN(rawValue)) {
            yearsExperience = rawValue;
            yearsExperienceText = String(rawValue);
        } else if (typeof rawValue === 'string') {
            const trimmed = rawValue.trim();
            if (trimmed.length > 0) {
                yearsExperienceText = trimmed;
            }

            const numericMatch = trimmed.match(/\d+(?:\.\d+)?/);
            if (numericMatch) {
                const parsed = Number.parseFloat(numericMatch[0]);
                if (!Number.isNaN(parsed)) {
                    yearsExperience = parsed;
                }
            }
        }
    }

    return {
        guide_id: guideRow.guide_id,
        full_name: guideRow.full_name,
        email: guideRow.email,
        phone: guideRow.phone,
        location: guideRow.location,
        bio: guideRow.bio,
        experience_types: guideRow.experience_types,
        support_types: guideRow.support_types,
        backgroundChecked: guideRow.backgroundChecked,
        avatarUrl,
        about,
        yearsExperience,
        yearsExperienceText,
    };
}

export async function matchSeekerWithGuides(request: MatchRequest): Promise<MatchResponse> {
    const sb = getSB();
    
    // Get current session for auth
    const { data: { session }, error: sessionError } = await sb.auth.getSession();
    
    if (sessionError || !session) {
        throw new Error('Authentication required');
    }

    const { data, error } = await sb.functions.invoke('match-seeker-guide', {
        body: request,
        headers: {
            Authorization: `Bearer ${session.access_token}`,
        },
    });

    if (error) {
        throw new Error(`Matching failed: ${error.message}`);
    }

    return data as MatchResponse;
}

export async function getMatchingRecommendations(seekerId: string, limit = 5): Promise<MatchScore[]> {
    try {
        const response = await matchSeekerWithGuides({ seeker_id: seekerId, limit });
        return response.matches;
    } catch (error) {
        console.error('Error getting matching recommendations:', error);
        throw error;
    }
}

export async function getStoredMatchForSeeker(seekerId: string): Promise<StoredMatch | null> {
    const sb = getSB();

    const { data, error } = await sb
        .from('seeker_onboarding_complete')
        .select(
            'seeker_id, latest_match_id, latest_match_guide_id, latest_match_score, latest_match_breakdown, latest_match_reasoning, latest_match_created_at, onboarding_completed_at'
        )
        .eq('seeker_id', seekerId)
        .order('onboarding_completed_at', { ascending: false, nullsFirst: false })
        .limit(1);

    if (error) {
        console.error('Failed to load stored match via onboarding view', error);
        throw new Error(error.message || 'Failed to load stored match');
    }

    const row = data?.[0];

    if (!row || !row.latest_match_id) {
        return null;
    }

    const guideDetails = await fetchGuideDetails(sb, row.latest_match_guide_id);

    let matchStatus: StoredMatchStatus = null;
    let matchEndedAt: string | null = null;
    try {
        const { data: matchRow, error: matchError } = await sb
            .from('matches')
            .select('status, ended_at')
            .eq('id', row.latest_match_id)
            .maybeSingle();
        if (matchError) {
            console.warn('Unable to load match status for admin view', matchError);
        } else if (matchRow) {
            matchStatus = (matchRow.status as StoredMatchStatus) ?? null;
            matchEndedAt = matchRow.ended_at ?? null;
        }
    } catch (statusErr) {
        console.warn('Unexpected error loading match status for admin view', statusErr);
    }

    return {
        id: row.latest_match_id,
        seeker_id: seekerId,
        guide_id: row.latest_match_guide_id,
        match_score: row.latest_match_score ?? 0,
        score_breakdown: (row.latest_match_breakdown as Record<string, number> | null) ?? null,
        reasoning: (row.latest_match_reasoning as string[] | null) ?? null,
        created_at: row.latest_match_created_at ?? row.onboarding_completed_at ?? new Date().toISOString(),
        updated_at: row.latest_match_created_at ?? null,
        status: matchStatus,
        ended_at: matchEndedAt,
        guide_details: guideDetails,
    };
}

export async function getSeekerMatchHistory(seekerId: string): Promise<StoredMatch[]> {
    const sb = getSB();

    const { data, error } = await sb
        .from('matches')
        .select('id, seeker_id, guide_id, match_score, score_breakdown, reasoning, created_at, updated_at, status, ended_at')
        .eq('seeker_id', seekerId)
        .order('created_at', { ascending: false });

    if (error) {
        console.error('Failed to load match history for seeker', error);
        throw new Error(error.message || 'Failed to load match history');
    }

    if (!data || data.length === 0) {
        return [];
    }

    const uniqueGuideIds = Array.from(
        new Set(
            data
                .map((row) => row.guide_id)
                .filter((guideId): guideId is string => Boolean(guideId))
        )
    );

    const guideDetailsCache = new Map<string, StoredMatch['guide_details']>();

    await Promise.all(
        uniqueGuideIds.map(async (guideId) => {
            const details = await fetchGuideDetails(sb, guideId);
            guideDetailsCache.set(guideId, details);
        })
    );

    // Fetch match exclusions for this seeker
    const exclusionsMap = new Map<string, MatchExclusionSummary>();

    type MatchExclusionRow = {
        seeker_id: string;
        guide_id: string;
        source: MatchChangeSource | null;
        reason_id?: string | null;
        note?: string | null;
        created_at?: string | null;
        expires_at?: string | null;
    };

    const { data: exclusionsRows, error: exclusionsError } = await sb
        .from('match_exclusions')
        .select('seeker_id, guide_id, source, reason_id, note, created_at, expires_at')
        .eq('seeker_id', seekerId);

    if (exclusionsError) {
        console.warn('Failed to load match exclusions for history view', exclusionsError);
    } else if (exclusionsRows && Array.isArray(exclusionsRows)) {
        const getTimestamp = (value?: string | null): number => {
            if (!value) return 0;
            const parsed = new Date(value).getTime();
            return Number.isNaN(parsed) ? 0 : parsed;
        };

        exclusionsRows.forEach((row: MatchExclusionRow) => {
            if (!row?.guide_id) return;
            const summary: MatchExclusionSummary = {
                seeker_id: row.seeker_id,
                guide_id: row.guide_id,
                source: row.source ?? null,
                reason_id: row.reason_id ?? null,
                note: row.note ?? null,
                created_at: row.created_at ?? null,
                expires_at: row.expires_at ?? null,
            };

            // Keep the most recent exclusion for each guide
            const existing = exclusionsMap.get(row.guide_id);
            if (!existing) {
                exclusionsMap.set(row.guide_id, summary);
                return;
            }

            const existingTimestamp = getTimestamp(existing.created_at);
            const incomingTimestamp = getTimestamp(summary.created_at);

            if (incomingTimestamp >= existingTimestamp) {
                exclusionsMap.set(row.guide_id, summary);
            }
        });
    }

    return data.map((row) => {
        const rawScore = typeof row.match_score === 'number' ? row.match_score : Number(row.match_score);

        return {
            id: row.id,
            seeker_id: row.seeker_id,
            guide_id: row.guide_id,
            match_score: Number.isFinite(rawScore) ? rawScore : 0,
            score_breakdown: (row.score_breakdown as Record<string, number> | null) ?? null,
            reasoning: (row.reasoning as string[] | null) ?? null,
            created_at: row.created_at ?? new Date().toISOString(),
            updated_at: row.updated_at ?? null,
            status: (row.status as StoredMatchStatus | null) ?? null,
            ended_at: row.ended_at ?? null,
            guide_details: row.guide_id ? guideDetailsCache.get(row.guide_id) ?? null : null,
            exclusion: row.guide_id ? exclusionsMap.get(row.guide_id) ?? null : null,
        };
    });
}

// Helper function to get match quality label
export function getMatchQuality(score: number): { label: string; color: string } {
    if (score >= 80) return { label: "Excellent Match", color: "#16a34a" };
    if (score >= 65) return { label: "Good Match", color: "#22c55e" };
    if (score >= 50) return { label: "Fair Match", color: "#eab308" };
    if (score >= 35) return { label: "Poor Match", color: "#f97316" };
    return { label: "No Match", color: "#dc2626" };
}

// Helper function to format reasoning
export function formatMatchReasoning(reasoning: string[]): string {
    return reasoning
        .filter(r => r && !r.includes("Missing") && !r.includes("unclear"))
        .join(" • ");
}
